import { alpha } from '@mui/material/styles';

import { BOTH_RADIUS } from '../shared/barRadius';
import type { EChartsTheme } from '../types';
import { buildComparisonHatchDecal, COMPARISON_FILL_ALPHA } from './decal';
import { BAR_LABEL_GAP, BAR_THICKNESS } from './layout';
import type { ComparisonBarChartConfig, ComparisonBarLayout, ComparisonBarRow } from './types';

/** Length of the marker standing in for a real zero. */
const ZERO_STUB_PX = 1;
/** Shortest a non-zero bar may render, so a tiny amount is still visibly a bar. */
const MIN_BAR_PX = BAR_THICKNESS;

/** Stagger between row reveals, and the row at which the stagger stops growing. */
const REVEAL_STAGGER_MS = 36;
const REVEAL_STAGGER_CAP = 40;

/** Series id prefixes, so the tooltip formatter can tell real bars from stubs. */
export const VALUE_SERIES_PREFIX = 'comparison-value';
export const STUB_SERIES_PREFIX = 'comparison-stub';

/**
 * Two series per range: the real bars, and the 1px stubs standing in for real zeros.
 *
 * The split exists because `barMinHeight` is per-series, and this chart needs two different floors —
 * 16px so a small amount is still a legible bar, and 1px so a zero is a tick rather than nothing.
 * One series cannot express both.
 *
 * The pair shares a `stack` id, which collapses them into a single column slot (ECharts keys its
 * stack map by that id), so a stub lands at exactly the position and thickness its real bar would
 * have had. Exactly one of the pair holds a value for any given (row, range); the other holds
 * `null` and draws nothing.
 */
export const buildComparisonBarSeries = (
  rows: ComparisonBarRow[],
  config: ComparisonBarChartConfig,
  layout: ComparisonBarLayout,
  theme: EChartsTheme,
) => {
  const { ranges, privacyMask, animated = true } = config;

  // Capped so a long list does not push the last row's reveal minutes into the future.
  const animationDelay = (dataIndex: number) =>
    animated ? Math.min(dataIndex, REVEAL_STAGGER_CAP) * REVEAL_STAGGER_MS : 0;

  return ranges.flatMap((range, rangeIndex) => {
    const isPrimary = rangeIndex === 0;
    const stack = `range-${range.id}`;

    const valueData = rows.map((row, rowIndex) => {
      const value = row.values[rangeIndex];
      // A zero belongs to the stub series; an absent cell belongs to neither.
      if (value == null || value === 0) return { id: `${row.id}-${range.id}`, value: null };

      const fill = isPrimary ? row.color : alpha(row.color, COMPARISON_FILL_ALPHA);

      return {
        // Row identity, so sorting and expanding animate as a reorder rather than a rebuild.
        id: `${row.id}-${range.id}`,
        name: row.name,
        value: Math.abs(value),
        itemStyle: {
          color: fill,
          // All four corners, per the design — the bars read as pills rather than as segments of a
          // longer track. Bars are magnitudes drawn from a common left origin, so there is no
          // left-growing case for `horizontalCornerRadiusFor` to handle either.
          borderRadius: BOTH_RADIUS,
          ...(isPrimary ? {} : { decal: buildComparisonHatchDecal(row.color, rangeIndex) }),
        },
        label: {
          show: true,
          position: 'right' as const,
          distance: BAR_LABEL_GAP,
          // Both amounts in the primary text colour; only the weight distinguishes them, matching
          // the design. A secondary-coloured comparison amount read as disabled.
          color: theme.textColor,
          fontWeight: isPrimary ? 500 : 400,
          fontSize: 14,
          formatter: () => String(privacyMask(row.labels[rangeIndex] ?? '')),
        },
        // Consumed by the tooltip formatter, which cannot reach back into `rows`.
        rowIndex,
      };
    });

    const stubData = rows.map((row) => {
      const value = row.values[rangeIndex];
      if (value !== 0) return { id: `${row.id}-${range.id}-stub`, value: null };
      return {
        id: `${row.id}-${range.id}-stub`,
        name: row.name,
        value: 0,
        itemStyle: {
          color: theme.textSecondaryColor,
          // A comparison-range zero is fainter still, matching the dashboard: the absence of last
          // period's spending is a weaker statement than the absence of this period's.
          opacity: isPrimary ? 1 : 0.5,
        },
      };
    });

    return [
      {
        id: `${VALUE_SERIES_PREFIX}-${range.id}`,
        type: 'bar' as const,
        name: range.label,
        stack,
        data: valueData,
        barWidth: layout.barWidth,
        barGap: layout.barGap,
        barMinHeight: MIN_BAR_PX,
        // Bars grow from the axis origin, which is geometrically the dashboard's left-to-right
        // clip-path reveal — so the staggered entrance needs no custom animation.
        animationDuration: animated ? 480 : 0,
        animationDelay,
        animationDurationUpdate: animated ? 300 : 0,
        animationEasingUpdate: 'cubicOut' as const,
        // The row-hover band is drawn in HTML behind the canvas, so ECharts must not also dim.
        emphasis: { disabled: true as const },
      },
      {
        id: `${STUB_SERIES_PREFIX}-${range.id}`,
        type: 'bar' as const,
        name: range.label,
        stack,
        data: stubData,
        barWidth: layout.barWidth,
        barGap: layout.barGap,
        barMinHeight: ZERO_STUB_PX,
        // A marker, not a datum: it must not take hover, tooltips, or a reveal of its own.
        silent: true,
        animation: false,
        tooltip: { show: false },
        emphasis: { disabled: true as const },
      },
    ];
  });
};
